import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { pickDefaultSchool } from "../lib/autoSelect";
import { localName } from "../lib/localName";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const ALL_SCHOOLS = "__all__";

/**
 * Hook that loads schools and applies role-based defaults:
 *  - super_admin: defaults to "all schools" (or first school if allowAll=false)
 *  - other roles: pinned to user.school_id
 */
export function useSchools({ allowAll = false } = {}) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");

  useEffect(() => {
    api.get("/schools").then((r) => {
      const list = r.data || [];
      setSchools(list);
      setSchoolId((prev) => {
        if (prev) return prev;
        if (isSuper) return allowAll ? ALL_SCHOOLS : (list[0]?.id || "");
        return pickDefaultSchool(list, user);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schoolName = (id) => {
    const s = schools.find((x) => x.id === id);
    return s ? localName(s, lang) : "";
  };

  // For non-super users, the effective id is always their own school
  const effectiveId = isSuper ? schoolId : (user?.school_id || "");
  // For API queries: null means "no filter" (all schools)
  const queryId = effectiveId === ALL_SCHOOLS ? null : effectiveId;

  return { schools, schoolId: effectiveId, setSchoolId, schoolName, isSuper, queryId };
}

export function SchoolSelect({
  schools,
  value,
  onChange,
  testId = "school-select",
  className = "w-56",
  disabled = false,
  showAll = false,
  allLabel,
}) {
  const { t, lang } = useI18n();
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={className} data-testid={testId}>
        <SelectValue placeholder={t("select_school")} />
      </SelectTrigger>
      <SelectContent>
        {showAll && (
          <SelectItem value={ALL_SCHOOLS} data-testid={`${testId}-all`}>
            {allLabel || t("hep_all_schools")}
          </SelectItem>
        )}
        {schools.map((s) => (
          <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
